import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Mail, Lock, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import api from '@/lib/api';
import LanguageToggle from '@/components/LanguageToggle';

const LoginPage = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  
  const { login, requestMagicLink, verifyMagicLink, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Handle magic link verification from URL
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      verifyMagicLink(token);
    }
  }, [searchParams, verifyMagicLink]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    
    setIsLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Login failed:', error);
      const errorMessage = error.response?.data?.detail || 'Invalid email or password';
      toast.error('Login failed', { description: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const handleMagicLink = async () => {
    if (!email) {
      toast.error('Please enter your email first');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await requestMagicLink(email);
      
      if (response.message) {
        setMagicLinkSent(true);
        toast.success('Login link sent!', { 
          description: 'Check your email for the login link' 
        });
      } else {
        toast.error('Account not found', { 
          description: 'No account exists with this email' 
        });
      }
    } catch (error) {
      console.error('Magic link request failed:', error);
      toast.error('Error', { 
        description: error.response?.data?.detail || 'Failed to send login link' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail) {
      toast.error('Please enter your email');
      return;
    }
    
    setIsLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: forgotEmail });
      setResetSent(true);
      toast.success('Password reset email sent!', {
        description: 'Check your inbox for the reset link'
      });
    } catch (error) {
      // Always show success to prevent email enumeration
      setResetSent(true);
      toast.success('If an account exists, you will receive an email');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      {/* Language Toggle - Top Right Corner */}
      <div className="absolute top-4 right-4">
        <LanguageToggle />
      </div>
      
      <div className="w-full max-w-md animate-fade-in">
        {/* Header - ContractorOS branding */}
        <div className="text-center mb-8">
          {/* ContractorOS Wordmark - Bold + Modern hybrid */}
          <h1 className="text-3xl font-black tracking-tight text-foreground">
            <span className="bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-transparent">Contractor</span>
            <span className="text-foreground">OS</span>
          </h1>
          <p className="text-xs text-muted-foreground mt-1 tracking-widest uppercase">{t('login.subtitle')}</p>
          <p className="text-muted-foreground text-lg mt-4">
            {t('login.signIn')}
          </p>
        </div>
        
        {!magicLinkSent ? (
          <form onSubmit={handlePasswordLogin} className="space-y-6">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              
              <div className="space-y-5">
                {/* Email Field */}
                <div>
                  <Label htmlFor="email" className="text-base mb-2 block font-medium">
                    {t('login.emailAddress')}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="h-12 px-4 text-base"
                    required
                    data-testid="login-email-input"
                  />
                </div>
                
                {/* Password Field */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="password" className="text-base font-medium">
                      {t('login.password')}
                    </Label>
                    <button
                      type="button"
                      onClick={() => {
                        setShowForgotPassword(true);
                        setForgotEmail(email);
                        setResetSent(false);
                      }}
                      className="text-sm text-primary hover:underline font-medium"
                    >
                      {t('login.forgotPassword')}
                    </button>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="h-12 px-4 text-base pr-12"
                      required
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                
                {/* Login Button */}
                <Button
                  type="submit"
                  disabled={isLoading || !email || !password}
                  className="w-full h-12 text-base font-semibold bg-primary hover:bg-primary/90"
                  data-testid="login-submit-btn"
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {t('login.signingIn')}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Lock className="w-5 h-5" />
                      {t('login.signInBtn')}
                    </span>
                  )}
                </Button>
                
                {/* Divider */}
                <div className="relative my-2">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-muted-foreground">{t('login.or')}</span>
                  </div>
                </div>
                
                {/* Magic Link Option */}
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleMagicLink}
                  disabled={isLoading || !email}
                  className="w-full h-12 text-base font-medium"
                  data-testid="magic-link-btn"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  {t('login.emailMeLink')}
                </Button>
              </div>
            </div>
          </form>
        ) : (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center animate-scale-in">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-green-600" />
            </div>
            
            <h2 className="font-heading text-2xl font-semibold mb-2">
              {t('login.checkEmail')}
            </h2>
            <p className="text-muted-foreground mb-6">
              {t('login.sentLinkTo')} <strong>{email}</strong>
            </p>
            
            <Button
              variant="ghost"
              onClick={() => {
                setMagicLinkSent(false);
              }}
              className="mt-4"
            >
              {t('login.useDifferentEmail')}
            </Button>
          </div>
        )}
        
        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground mt-8">
          Powered by{' '}
          <a href="https://birdenmedia.co" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
            BirdenMedia
          </a>
        </p>
      </div>
      
      {/* Forgot Password Modal */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('login.resetPassword')}</DialogTitle>
          </DialogHeader>
          
          {!resetSent ? (
            <div className="space-y-4 mt-4">
              <p className="text-sm text-muted-foreground">
                {t('login.resetDesc')}
              </p>
              
              <div className="space-y-2">
                <Label htmlFor="forgot-email">{t('login.emailAddress')}</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="h-11"
                  data-testid="forgot-email-input"
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowForgotPassword(false)}
                  className="flex-1"
                >
                  {t('login.cancel')}
                </Button>
                <Button
                  onClick={handleForgotPassword}
                  disabled={isLoading || !forgotEmail}
                  className="flex-1"
                  data-testid="send-reset-btn"
                >
                  {isLoading ? t('login.sending') : t('login.sendResetLink')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 mt-4 text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <Mail className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">{t('login.checkEmail')}</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('login.ifAccountExists')} <strong>{forgotEmail}</strong>{t('login.willReceiveLink')}
                </p>
              </div>
              <Button
                onClick={() => {
                  setShowForgotPassword(false);
                  setResetSent(false);
                }}
                className="mt-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('login.backToLogin')}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
